import type { BillingContextResponse, ProjectId } from "@storytell/types";
import type { Accessor } from "solid-js";
import { useWire } from "~/wire";

interface UseProjectBillingContextResult {
  data: Accessor<BillingContextResponse | undefined>;
  projectName: () => string | undefined;
}

/**
 * The billing context only while it belongs to the project it was asked for.
 *
 * The resource keeps serving the previous project's payload while the next one
 * is in flight, so an unfiltered read answers "what is this project's balance"
 * with another project's — which is a wrong balance on screen and, for the
 * credit gates, a block or a pass decided against the project the user left.
 */
export const billingContextForProject = (
  context: BillingContextResponse | undefined,
  projectId: string | undefined
): BillingContextResponse | undefined =>
  context !== undefined && projectId !== undefined && context.projectId === projectId ? context : undefined;

export function useProjectBillingContext(projectId: () => string | undefined): UseProjectBillingContextResult {
  const wire = useWire();

  return {
    // Read through rather than memoised: the resource read has to happen in the
    // caller's own tracking scope for a Suspense boundary around it to see it.
    data: () => billingContextForProject(wire.projectBilling.data(), projectId()),
    projectName: () => {
      const id = projectId();
      if (!id) return undefined;
      return wire.projects.get?.(id as ProjectId)?.name;
    },
  };
}
